import {
  TextFieldProps,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Autocomplete,
  Checkbox,
  TextField,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import {
  Controller,
  FieldValues,
  Control,
  Path,
  useFormContext,
} from "react-hook-form";
import { useEffect, useState } from "react";
import { InputField } from "@src/shared/ui/InputField";
import crossIcon from "@src/shared/assets/cross_icon.svg";
import openIcon from "@src/shared/assets/open_icon.svg";
import { useGetFilters } from "@src/entities/requestHH/model/hooks/useGetFilters/useGetFilters";
import CheckIcon from "@mui/icons-material/Check";
import { numbersOnly } from "@src/shared/utils";
import HelpIcon from "@mui/icons-material/Help";
import { ExclusiveCheckboxGroup } from "../ExclusiveCheckboxGroup/ExclusiveCheckboxGroup";

import styles from "./CandidateProfile.module.scss";

const crossImg = <img src={crossIcon} />;
const openImg = <img src={openIcon} className={styles.openImg} />;

export type TField<T extends FieldValues> = {
  name: Path<T>;
  label?: string;
  rules?: object;
  className?: string;
  slotProps?: TextFieldProps["slotProps"];
  helperText?: string;
  type?: React.HTMLInputTypeAttribute | undefined;
  disabled?: boolean;
  errors?: boolean | undefined;
  placeholder?: string;
};

type TEducationOption = {
  id: string;
  name: string;
};

interface TСandidateProfileFieldProps<T extends FieldValues> {
  control: Control<T>;
  field1: TField<T>;
  field2: TField<T>;
  field3: TField<T>;
  field4: TField<T>;
  field5: TField<T>;
  field6: TField<T>;
  field7: TField<T>;
  field8: TField<T>;
  isDisabledBtns: boolean;
  isDisabledField: boolean;
}

export const CandidateProfile = <T extends FieldValues>({
  control,
  field1,
  field2,
  field3,
  field4,
  field5,
  field6,
  field7,
  field8,
  // isDisabledBtns,
  isDisabledField,
}: TСandidateProfileFieldProps<T>) => {
  const [expanded, setExpanded] = useState(true);
  const { data: filters } = useGetFilters();
  const { education_types, genders } = filters?.data || {};
  const EDUCATION_OPTIONS = (education_types || []) as TEducationOption[];
  const PROFILE_EDUCATION_IDS = EDUCATION_OPTIONS.filter((item) =>
    item.id.includes("HIGHER_"),
  ).map((item) => item.id);
  const GENDER_OPTIONS = genders || [];
  const { trigger, watch, setValue } = useFormContext();

  const ageFrom = watch("ageFrom");
  const ageTo = watch("ageTo");

  const isProfileEducation = (educationValue?: TEducationOption | null) => {
    if (!educationValue?.id) {
      return false;
    }

    return PROFILE_EDUCATION_IDS.includes(educationValue.id);
  };

  useEffect(() => {
    trigger(["ageFrom", "ageTo"]);
  }, [ageFrom, ageTo, trigger]);

  const education = watch("education") as TEducationOption | null;
  const isDisabledEduOptional = !isProfileEducation(education);
  const disabledEduOptionalByEducation = isDisabledEduOptional;
  const disabledEduOptional = isDisabledEduOptional || isDisabledField;

  const disabledTooltipText = disabledEduOptionalByEducation
    ? "Сначала выберите профильное высшее образование"
    : "";
  const helperTextEduOptionalImg =
    "Можно включить только при выборе профильного высшего образования";

  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <Accordion
      defaultExpanded
      expanded={expanded}
      sx={{
        mt: 0,
        backgroundColor: "#ffffff",
        borderRadius: "25px",
        padding: "17px 30px",
        color: "rgba(38, 38, 38, 1)",
        boxShadow: "none",
        "&:before": {
          display: "none",
        },
        "&.MuiAccordion-root": {
          marginTop: "0px !important",
          borderRadius: "25px",
        },
      }}
      className={styles.wrapper}
    >
      <AccordionSummary
        sx={{
          backgroundColor: "inherit",
          height: "30px",
          px: 0,
          "& .MuiAccordionSummary-content": {
            margin: 0,
          },
        }}
      >
        <Box
          onClick={handleToggle}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            cursor: "pointer",
            width: "100%",
            height: "30px",
            fontSize: "20px",
            fontWeight: 600,
          }}
        >
          <h3>Профиль кандидата</h3>
          <div className={styles.iconWrapper}>
            {expanded ? (
              <ExpandLess sx={{ color: "rgba(164, 187, 199, 1)" }} />
            ) : (
              <ExpandMore sx={{ color: "rgba(164, 187, 199, 1)" }} />
            )}
          </div>
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: 0,
          backgroundColor: "inherit",
          py: 0,
          mt: 0,
          mb: 0,
          gap: 0,
          borderTop: "1px solid rgba(164, 187, 199, 0.25)",
          paddingTop: "20px",
        }}
      >
        <div className={styles.ageWrapper}>
          <div className={styles.ageTitle}>
            <span>Опыт работы (лет):</span>
          </div>
          <div className={styles.ageFields}>
            <InputField
              control={control}
              name={field1.name}
              placeholder="От"
              className={styles.inputFieldForAge}
              inputFilter={numbersOnly}
              disabled={isDisabledField}
            />
            <span className={styles.def}>—</span>
            <InputField
              control={control}
              name={field7.name}
              placeholder="До"
              className={styles.inputFieldForAge}
              inputFilter={numbersOnly}
              disabled={isDisabledField}
            />
          </div>
        </div>
        <Controller
          name={field2.name}
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <div className={styles.educationTitle}>
                <span>Образование:</span>
                <div className={styles.profilePreferredInfo}>
                  <Tooltip
                    title={
                      "Указанное профильное образование является желательным, наличие любого высшего — обязательно"
                    }
                    arrow
                  >
                    <div className={styles.profilePreferredIcon}>
                      <HelpIcon className={styles.profilePreferredImg} />
                    </div>
                  </Tooltip>
                </div>
              </div>
              <Autocomplete<TEducationOption, false, false, false>
                options={EDUCATION_OPTIONS}
                className={styles.autocomplete}
                disabled={isDisabledField}
                clearIcon={crossImg}
                popupIcon={openImg}
                getOptionLabel={(option) => {
                  return typeof option === "object" ? option.name : option;
                }}
                isOptionEqualToValue={(option, val) => option.id === val.id}
                value={value}
                onChange={(_, newValue) => {
                  if (!isProfileEducation(newValue)) {
                    setValue("isEduOptional", false, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }

                  onChange(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Выберите образование"
                    variant="outlined"
                    className={styles.inputField}
                    sx={{
                      borderRadius: "40px",
                      "& input": {
                        color: "rgba(38, 38, 38, 1)",
                      },
                      "& .MuiOutlinedInput-root": {
                        background: "rgba(164, 187, 199, 0.25)",
                      },
                    }}
                  />
                )}
                sx={{
                  mt: 2,
                  ".MuiAutocomplete-popupIndicator": { color: "#fff" },
                  ".MuiSvgIcon-root": { color: "#fff" },
                  color: "#fff",
                }}
              />
            </>
          )}
        />
        <div className={styles.profilePreferred}>
          <Controller
            name={field8.name}
            control={control}
            render={({ field }) => (
              <Tooltip
                title={disabledTooltipText}
                arrow
                disableHoverListener={!disabledEduOptionalByEducation}
                disableFocusListener={!disabledEduOptionalByEducation}
                disableTouchListener={!disabledEduOptionalByEducation}
              >
                <span className={styles.profilePreferredCheckboxWrapper}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={Boolean(field.value)}
                        disabled={disabledEduOptional}
                        onChange={(event) =>
                          field.onChange(event.target.checked)
                        }
                        icon={<span className={styles.customBox} />}
                        checkedIcon={
                          <span
                            className={`${styles.customBox} ${styles.checked}`}
                          >
                            <CheckIcon />
                          </span>
                        }
                      />
                    }
                    label="Желательно"
                    className={styles.salaryLabel}
                  />
                </span>
              </Tooltip>
            )}
          />

          <div className={styles.profilePreferredInfo}>
            <Tooltip title={helperTextEduOptionalImg} arrow>
              <div className={styles.profilePreferredIcon}>
                <HelpIcon className={styles.profilePreferredImg} />
              </div>
            </Tooltip>
          </div>
        </div>

        <div className={styles.genderField}>
          <span className={styles.label}>Пол:</span>

          <ExclusiveCheckboxGroup
            control={control}
            name={field3.name}
            options={GENDER_OPTIONS}
            disabled={isDisabledField}
          />
        </div>
        <div className={styles.ageWrapper}>
          <div className={styles.ageTitle}>
            <span>Возраст кандидата:</span>
          </div>
          <div className={styles.ageFields}>
            <InputField
              control={control}
              name={field4.name}
              placeholder="От"
              className={styles.inputFieldForAge}
              inputFilter={numbersOnly}
              disabled={isDisabledField}
            />
            <span className={styles.def}>—</span>
            <InputField
              control={control}
              name={field5.name}
              placeholder="До"
              className={styles.inputFieldForAge}
              inputFilter={numbersOnly}
              disabled={isDisabledField}
            />
          </div>
        </div>
        <div className={styles.jobChanges}>
          <Controller
            name={field6.name}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    disabled={isDisabledField}
                    checked={field.value}
                    icon={<span className={styles.customBox} />}
                    checkedIcon={
                      <span className={`${styles.customBox} ${styles.checked}`}>
                        <CheckIcon />
                      </span>
                    }
                  />
                }
                label="Показывать кандидатов без указанного возраста"
                className={styles.salaryLabel}
              />
            )}
          />
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
